import { NextRouter } from 'next/router';
import {
    buildQueryParamsString,
    buildUrlFromPathAndQueryParams
} from '@zoopla/listing-search-utils';
import { hasHidePolygon } from '@zoopla/listing-search-has-polygon';
import { getNewPath } from './getNewPath';
import { updateQueryParams } from './updateQueryParams';
import { searchPathBuilder } from './types';
import { SearchSource } from '@zoopla/listing-search-context';

type SearchPageType = 'for-sale' | 'to-rent' | 'new-homes';

const getPathBuilder = (
    asPath: string,
    searchType: SearchPageType
): searchPathBuilder => {
    const path = asPath.split('?').shift() || '';
    const splitPath = path.split('/');

    const hasSearchTypeInPath = splitPath.includes(searchType);
    const hasMapInPath = splitPath.includes('map');

    if (!hasSearchTypeInPath)
        return {
            host: '',
            searchType,
            maps: hasMapInPath,
            propertyType: 'property',
            extra: []
        };

    const newPathArray = splitPath.slice(0, 3);

    return {
        host: newPathArray[0],
        searchType: newPathArray[1],
        maps: hasMapInPath,
        propertyType: hasMapInPath ? newPathArray[3] : newPathArray[2],
        extra: []
    };
};

export const updateResults = async (
    // eslint-disable-next-line
    filters: any,
    router: NextRouter,
    searchType: SearchPageType,
    searchSource: SearchSource,
    sortOrder: string,
    userAlertId: string | null = null,
    excludeRadius = false,
    ignoreLocationText = false,
    page = 1,
    excludePageSize = false
) => {
    const {
        location,
        locationChanged,
        propertyType,
        propertySubType,
        newHomes,
        retirementHome,
        auction,
        locationText: filterLocationText
    } = filters;

    const { asPath, push, query } = router;

    const hasHidePoly = hasHidePolygon(asPath);

    const newPathBuilder = getPathBuilder(asPath, searchType);

    const isBungalow = propertySubType.includes('bungalow');
    const isPropertyTypeFlatsOrHouses =
        propertyType === 'houses' || propertyType === 'flats';

    const locationText = !ignoreLocationText ? filterLocationText : '';

    // Only need this block if no locationText due to the navigation conditions below
    if (locationText === '') {
        if (newHomes === 'only') {
            newPathBuilder.searchType = 'new-homes';
        }

        if (!newHomes && searchType === 'new-homes') {
            newPathBuilder.searchType = 'for-sale';
        }

        if (!isBungalow && isPropertyTypeFlatsOrHouses) {
            newPathBuilder.propertyType = propertyType as string;
        } else if (isBungalow && propertyType === 'flats') {
            newPathBuilder.propertyType = 'flats';
        } else if (
            isBungalow &&
            propertySubType.length > 1 &&
            propertyType === 'houses'
        ) {
            newPathBuilder.propertyType = 'houses';
        } else if (
            isBungalow &&
            propertySubType.length === 1 &&
            propertyType === 'houses'
        ) {
            newPathBuilder.propertyType = 'bungalows';
        } else {
            newPathBuilder.propertyType = 'property';
        }

        if (!isPropertyTypeFlatsOrHouses && retirementHome === 'true') {
            newPathBuilder.propertyType = 'retirement';
        }

        if (!isPropertyTypeFlatsOrHouses && auction === 'true') {
            if (newPathBuilder.propertyType === 'property') {
                newPathBuilder.propertyType = 'auction';
            } else {
                newPathBuilder.extra.push('auction');
            }
        }

        newPathBuilder.extra.push(location.id);
    }

    const polyenc =
        query?.polyenc && locationChanged === 'false'
            ? query.polyenc
            : undefined;
    const hidePolyValue = hasHidePoly && locationChanged === 'false';

    const newQuery = updateQueryParams(
        filters,
        searchType,
        searchSource,
        sortOrder,
        isPropertyTypeFlatsOrHouses,
        isBungalow,
        userAlertId,
        excludeRadius,
        locationText,
        newPathBuilder.maps,
        polyenc,
        excludePageSize,
        hidePolyValue
    );

    const newPath = getNewPath(newPathBuilder);
    const nextQueryParameters = buildQueryParamsString(newQuery, page);

    let nextUrl;
    if (!locationText) {
        nextUrl = buildUrlFromPathAndQueryParams(newPath, nextQueryParameters);
        await push(nextUrl);
    } else {
        nextUrl = buildUrlFromPathAndQueryParams(
            '/search',
            nextQueryParameters
        );
        await push(nextUrl);
    }

    return nextUrl;
};
